/**
 * USER-SUPPLIED EQUIPMENT TIER — unverified equipment the user adds themselves,
 * separate from the shipped, source-reviewed screening catalog.
 *
 * DESIGN (mirrors the catalog's solver-integrity rules without borrowing its
 * provenance):
 *  - The normalization math is IDENTICAL to the shipped catalog convention
 *    (net→ISO-gross via the auxiliary fraction; LHV→HHV via the disclosed EPA
 *    screening ratio; heat rate rebased so ISO fuel input is conserved), so a
 *    user entry and a catalog entry with the same published numbers produce
 *    the same solver duty.
 *  - Applying a user entry NEVER writes a `GenerationEquipmentBasis`. All duty
 *    lands through assumptions with `source: 'user'` and low confidence, so
 *    exhibits and reports cannot present it as vendor-published data.
 *  - Water and footprint planning values are left untouched: a user entry
 *    carries no cooling/land data, and silently swapping surrogates would be a
 *    hidden physics change.
 *
 * Pure + deterministic: same entry → same duty.
 */
import type {
  GenerationAssumption,
  GenerationImpactResult,
  GenerationImpactScenarioInput,
  GenerationUserEquipmentEntry,
} from '../../types/generationImpact';
import {
  BTU_PER_KILOJOULE,
  NATURAL_GAS_LHV_TO_HHV_RATIO,
} from './generationEquipmentCatalog';
import { solveGenerationImpact } from './solveGenerationImpact';
import { hashCanonicalJson } from '../reportGeneration/reportContractHash';

/**
 * The standard OEM/EPC data-request list attached to every user entry. This is
 * the union of what the sourced catalog tracks per-entry — a user entry starts
 * with ALL of it open because nothing has been verified.
 */
export const USER_EQUIPMENT_STANDARD_DATA_REQUESTS: readonly string[] = [
  'Contractual net and gross plant rating with reference conditions',
  'Guaranteed heat rate, heating-value basis, and fuel specification envelope',
  'Site ambient and altitude correction curves',
  'Required fuel-gas inlet pressure and composition envelope',
  'Installed plant dimensions, maintenance clearances, and laydown',
  'Cooling-system heat balance and contractual water balance',
  'Octave-band sound-power data and guaranteed boundary noise',
  'CCS integration derate, tie-ins, and capture footprint',
  'Contractual availability, maintenance schedule, and common-cause outage basis',
  'Current production-slot availability and indicative lead time',
];

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Engineering-range validation; returns [] when the entry is usable. */
export function validateGenerationUserEquipmentEntry(
  entry: GenerationUserEquipmentEntry,
): readonly string[] {
  const errors: string[] = [];
  const requiredText: Array<[string, string, number]> = [
    ['manufacturer', entry.manufacturer, 120],
    ['model', entry.model, 120],
    ['train configuration', entry.trainConfiguration, 200],
  ];
  requiredText.forEach(([label, value, max]) => {
    // MISSING and TOO LONG are different problems and must read differently.
    // Collapsing them into "is required" made a visibly-filled field report as
    // empty — the imported BOM `spec` string routinely overruns these caps, so
    // the user saw text in the box and an error saying it was missing.
    if (typeof value !== 'string' || value.trim().length === 0) {
      errors.push(`${label} is required`);
      return;
    }
    if (value.length > max) {
      errors.push(`${label} is too long — ${value.length} characters, max ${max}. Trim it to the essentials.`);
    }
  });
  if (!Number.isInteger(entry.unitsPerBlock) || entry.unitsPerBlock < 1 || entry.unitsPerBlock > 100) {
    errors.push('units per block must be an integer between 1 and 100');
  }
  if (!Number.isFinite(entry.ratedOutputMWPerBlock)
    || entry.ratedOutputMWPerBlock < 0.5
    || entry.ratedOutputMWPerBlock > 2_000) {
    errors.push('rated output must be between 0.5 and 2,000 MW per block');
  }
  if (entry.heatRateValue == null || entry.heatRateUnit == null || entry.heatingValueBasis == null) {
    errors.push('heat rate value, unit, and heating-value basis are required for screening');
  } else {
    const kjPerKWh = entry.heatRateUnit === 'kJ/kWh'
      ? entry.heatRateValue
      : entry.heatRateValue / BTU_PER_KILOJOULE;
    if (!Number.isFinite(kjPerKWh) || kjPerKWh < 5_000 || kjPerKWh > 15_000) {
      errors.push('heat rate must be between 5,000 and 15,000 kJ/kWh (or the Btu/kWh equivalent) — check the unit');
    }
  }
  if (!Number.isFinite(entry.auxiliaryFractionOfIsoGross)
    || entry.auxiliaryFractionOfIsoGross < 0
    || entry.auxiliaryFractionOfIsoGross > 0.25) {
    errors.push('auxiliary fraction must be between 0% and 25% of ISO gross');
  }
  (['ambientFactor', 'degradationFactor'] as const).forEach(field => {
    const value = entry[field];
    if (!Number.isFinite(value) || value <= 0.5 || value > 1) {
      errors.push(`${field === 'ambientFactor' ? 'ambient factor' : 'degradation factor'} must be in (0.5, 1]`);
    }
  });
  if (entry.oemContactEmail != null
    && entry.oemContactEmail !== ''
    && !EMAIL_PATTERN.test(entry.oemContactEmail)) {
    errors.push('OEM contact email is not a valid address');
  }
  if (entry.importSource != null
    && (typeof entry.importSource.label !== 'string'
      || entry.importSource.label.trim().length === 0
      || entry.importSource.label.length > 200)) {
    errors.push('import source label must be a non-empty string of at most 200 characters');
  }
  return errors;
}

export interface GenerationUserEquipmentDuty {
  solverIsoGrossMWPerBlock: number;
  solverHeatRateBtuPerKWhHhv: number;
  normalizationNote: string;
}

/**
 * Normalize a user entry to the canonical solver convention (ISO gross output,
 * HHV heat rate on gross). Identical algebra to the shipped catalog:
 * net-plant ratings are un-derated by the auxiliary fraction and their heat
 * rate is rebased by net/gross so ISO fuel input is conserved; gross and
 * generator-terminal ratings pass through with the auxiliary load applied
 * explicitly by the solver.
 */
export function normalizeGenerationUserEquipmentDuty(
  entry: GenerationUserEquipmentEntry,
): GenerationUserEquipmentDuty {
  if (entry.heatRateValue == null || entry.heatRateUnit == null || entry.heatingValueBasis == null) {
    throw new RangeError('User equipment requires a heat rate value, unit, and heating-value basis.');
  }
  const heatRateBtu = entry.heatRateUnit === 'kJ/kWh'
    ? entry.heatRateValue * BTU_PER_KILOJOULE
    : entry.heatRateValue;
  const heatRateBtuHhv = entry.heatingValueBasis === 'LHV'
    ? heatRateBtu / NATURAL_GAS_LHV_TO_HHV_RATIO
    : heatRateBtu;
  const netFraction = 1 - entry.auxiliaryFractionOfIsoGross;
  const isNetScope = entry.ratingScope === 'net-plant';
  const solverIsoGrossMWPerBlock = isNetScope
    ? entry.ratedOutputMWPerBlock / netFraction
    : entry.ratedOutputMWPerBlock;
  const solverHeatRateBtuPerKWhHhv = isNetScope
    ? heatRateBtuHhv * netFraction
    : heatRateBtuHhv;
  const scopeNote = isNetScope
    ? `The ${entry.ratedOutputMWPerBlock} MW user-entered net rating is normalized to solver gross output with the ${(entry.auxiliaryFractionOfIsoGross * 100).toFixed(1)}% auxiliary fraction, and the heat rate is rebased by net/gross so fuel input is conserved.`
    : `The ${entry.ratedOutputMWPerBlock} MW user-entered ${entry.ratingScope.replaceAll('-', ' ')} rating is used directly as solver ISO gross; the ${(entry.auxiliaryFractionOfIsoGross * 100).toFixed(1)}% auxiliary fraction is applied as an explicit load.`;
  const hhvNote = entry.heatingValueBasis === 'LHV'
    ? ` LHV heat rate converted with the EPA screening ratio LHV/HHV = ${NATURAL_GAS_LHV_TO_HHV_RATIO}.`
    : '';
  return {
    solverIsoGrossMWPerBlock,
    solverHeatRateBtuPerKWhHhv,
    normalizationNote: `UNVERIFIED user-supplied equipment — no OEM source on record. ${scopeNote}${hhvNote} Water and land planning values are unchanged from the prior scenario basis.`,
  };
}

function physicalBlockCount(input: GenerationImpactScenarioInput): number {
  return input.units.reduce(
    (sum, unit) => sum + (Number.isInteger(unit.count) && unit.count > 0 ? unit.count : 0),
    0,
  );
}

function replaceUserEquipmentAssumptions(
  assumptions: readonly GenerationAssumption[],
  entry: GenerationUserEquipmentEntry,
  duty: GenerationUserEquipmentDuty,
): GenerationAssumption[] {
  const retained = assumptions.filter(assumption => (
    assumption.field !== 'technologyArchetype'
    && !assumption.field.startsWith('equipment')
    && !assumption.field.startsWith('projectDuty')
  ));
  return [
    ...structuredClone(retained),
    {
      field: 'technologyArchetype',
      value: entry.technology,
      source: 'user',
      label: `${entry.manufacturer} ${entry.model} user-supplied equipment identity (unverified)`,
      confidence: 'low',
    },
    {
      field: 'equipmentUserRating',
      value: entry.ratedOutputMWPerBlock,
      source: 'user',
      label: `${entry.ratingScope.replaceAll('-', ' ')} user-entered output in MW per block`,
      confidence: 'low',
    },
    {
      field: 'equipmentScreeningNormalization',
      value: duty.solverIsoGrossMWPerBlock,
      source: 'user',
      label: 'Solver ISO-gross normalization in MW per block',
      citation: duty.normalizationNote,
      confidence: 'low',
    },
    {
      field: 'equipmentDataGaps',
      value: USER_EQUIPMENT_STANDARD_DATA_REQUESTS.join('; '),
      source: 'planning-default',
      label: 'OEM / EPC data request outstanding for user-supplied equipment',
      confidence: 'low',
    },
  ];
}

/**
 * The applied-duty fingerprint for a user-supplied entry.
 *
 * The user-equipment counterpart of the catalog `appliedDutyFingerprint`, and
 * the thing that makes a project duty dossier bindable to an unverified entry:
 * a dossier's whole restoration contract depends on being able to reproduce the
 * EXACT pre-dossier duty, which needs an immutable fingerprint to restore to.
 * `applyGenerationUserEquipmentEnvelope` is fully deterministic, so this hashes
 * precisely the unit fields that path writes — no more, so an unrelated entry
 * edit cannot silently invalidate a bound dossier, and no less, so a duty change
 * always does.
 *
 * Deliberately NOT byte-compatible with the catalog fingerprint: the two are
 * different provenance classes and must never collide.
 */
export function expectedUserEquipmentDutyFingerprint(
  entry: GenerationUserEquipmentEntry,
  blockCount: number,
): string {
  const duty = normalizeGenerationUserEquipmentDuty(entry);
  return hashCanonicalJson({
    kind: 'user-equipment-applied-duty-v1',
    entryId: entry.id,
    units: Array.from({ length: blockCount }, (_, index) => ({
      technology: entry.technology,
      contingencyGroupId: `user-equipment-group-${index + 1}`,
      count: 1,
      isoGrossMWPerUnit: duty.solverIsoGrossMWPerBlock,
      ambientFactor: entry.ambientFactor,
      degradationFactor: entry.degradationFactor,
      auxiliaryLoadMWPerUnit:
        duty.solverIsoGrossMWPerBlock * entry.auxiliaryFractionOfIsoGross,
      heatRateBtuPerKWhHhv: duty.solverHeatRateBtuPerKWhHhv,
    })),
  });
}

/**
 * Apply one user-supplied equipment entry as the working screening duty.
 * Site evidence, CCS, parcels, receptors, lighting, water, and footprint are
 * untouched. Both catalog authorities are cleared — an unverified entry can
 * never masquerade as a sourced equipment basis or project duty dossier.
 */
export function applyGenerationUserEquipmentEnvelope(
  source: GenerationImpactScenarioInput,
  entryId: string,
): GenerationImpactScenarioInput {
  const entry = source.equipmentWorkspace?.userEquipment
    .find(candidate => candidate.id === entryId);
  if (!entry) throw new RangeError(`Unknown user equipment entry: ${entryId}`);
  const validationErrors = validateGenerationUserEquipmentEntry(entry);
  if (validationErrors.length > 0) {
    throw new RangeError(`User equipment entry is not usable: ${validationErrors.join('; ')}`);
  }
  const blockCount = physicalBlockCount(source);
  if (!Number.isInteger(blockCount) || blockCount < 1 || blockCount > 1_000) {
    throw new RangeError('Equipment selection requires between 1 and 1,000 physical generation blocks.');
  }
  const duty = normalizeGenerationUserEquipmentDuty(entry);
  const applied = structuredClone(source);
  applied.units = Array.from({ length: blockCount }, (_, index) => ({
    id: `user-equipment-block-${index + 1}`,
    label: `${entry.model} block ${index + 1}`,
    technology: entry.technology,
    contingencyGroupId: `user-equipment-group-${index + 1}`,
    count: 1,
    isoGrossMWPerUnit: duty.solverIsoGrossMWPerBlock,
    ambientFactor: entry.ambientFactor,
    degradationFactor: entry.degradationFactor,
    auxiliaryLoadMWPerUnit:
      duty.solverIsoGrossMWPerBlock * entry.auxiliaryFractionOfIsoGross,
    heatRateBtuPerKWhHhv: duty.solverHeatRateBtuPerKWhHhv,
  }));
  delete applied.noiseSources;
  delete applied.equipmentBasis;
  delete applied.projectDutyBasis;
  applied.assumptions = replaceUserEquipmentAssumptions(applied.assumptions, entry, duty);
  // Workspace metadata (never an authority claim): remembers which user entry
  // supplied the working duty so capacity can resize the fleet by block count.
  applied.equipmentWorkspace = {
    shortlistedCatalogIds: applied.equipmentWorkspace?.shortlistedCatalogIds ?? [],
    userEquipment: applied.equipmentWorkspace?.userEquipment ?? [],
    appliedUserEquipmentId: entry.id,
  };
  return applied;
}

export interface GenerationUserEquipmentScreenRow {
  entry: GenerationUserEquipmentEntry;
  /** Non-empty when the entry cannot be screened; solver fields are then null. */
  validationErrors: readonly string[];
  duty: GenerationUserEquipmentDuty | null;
  siteGrossMWPerBlock: number | null;
  siteNetMWPerBlock: number | null;
  result: GenerationImpactResult | null;
}

/** Screen every user entry through the unchanged canonical solver. */
export function buildGenerationUserEquipmentScreen(
  input: GenerationImpactScenarioInput,
): readonly GenerationUserEquipmentScreenRow[] {
  const entries = input.equipmentWorkspace?.userEquipment ?? [];
  return entries.map(entry => {
    const validationErrors = validateGenerationUserEquipmentEntry(entry);
    if (validationErrors.length > 0) {
      return {
        entry,
        validationErrors,
        duty: null,
        siteGrossMWPerBlock: null,
        siteNetMWPerBlock: null,
        result: null,
      };
    }
    const candidate = applyGenerationUserEquipmentEnvelope(input, entry.id);
    const result = solveGenerationImpact(candidate);
    return {
      entry,
      validationErrors: [],
      duty: normalizeGenerationUserEquipmentDuty(entry),
      siteGrossMWPerBlock: result.unitResults[0]?.siteGrossMWPerUnit ?? 0,
      siteNetMWPerBlock: result.unitResults[0]?.netMWPerUnit ?? 0,
      result,
    };
  });
}
